import { Close, Search } from "@mui/icons-material";
import { IconButton, InputBase, Paper } from "@mui/material";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Constants
const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_DELAY = 500;
const EXPANDED_WIDTH = 350;
const COLLAPSED_WIDTH = 54;

export default function IndieGridSearch({ onSearch }) {
    const { t } = useTranslation();

    const [expanded, setExpanded] = useState(false);
    const [value, setValue] = useState("");
    const [hasCleared, setHasCleared] = useState(false);
    const inputRef = useRef(null);

    // Memoize the debounced search function to prevent recreation on every render
    const debouncedSearch = useMemo(
        () => _.debounce((searchValue) => {
            onSearch(1, [
                {
                    id: "global_search",
                    value: searchValue
                }
            ]);
        }, DEBOUNCE_DELAY),
        [onSearch]
    );

    // Cleanup debounced function on unmount
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    useEffect(() => {
        if (value.length >= MIN_SEARCH_LENGTH) {
            setHasCleared(false);
            debouncedSearch(value);
        } else if (value.length === 0 && !hasCleared) {
            // Only clear search results once when value becomes empty
            setHasCleared(true);
            debouncedSearch("");
        }
        // Don't make API calls for lengths 1-2 characters
    }, [value]);

    const handleToggle = useCallback(() => setExpanded((prev) => !prev), []);

    const handleClose = useCallback(() => {
        setExpanded(false);
        setValue("");
        setHasCleared(false);
        debouncedSearch.cancel(); // Cancel any pending search
    }, [debouncedSearch]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === "Escape") {
            handleClose();
        }
    }, [handleClose]);

    const handleInputChange = useCallback((e) => {
        setValue(e.target.value);
    }, []);

    return (
        <Paper
            elevation={0}
            sx={{
                display: "flex",
                alignItems: "center",
                px: 0.7,
                py: 0.3,
                width: expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
                transition: "width 0.3s ease",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
            }}
        >
            <IconButton
                size="small"
                onClick={expanded ? handleClose : handleToggle}
            >
                {expanded ? <Close /> : <Search />}
            </IconButton>

            <InputBase
                ref={inputRef}
                autoFocus={expanded}
                value={value}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={t("placeholder.search.community")}
                sx={{
                    ml: 1,
                    flex: 1,
                    opacity: expanded ? 1 : 0,
                    transition: "opacity 0.2s ease",
                    pb: "1px",
                }}
            />
        </Paper>
    );
}
